import styled, { css } from "styled-components";

export interface WrapperProps {
  position?: "static" | "relative" | "absolute" | "fixed" | "sticky" | "inherit";
  display?: string;

  flex?: string;
  flexGrow?: number;
  wrap?: "nowrap" | "wrap" | "wrap-reverse";
  align?: "initial" | "flex-start" | "flex-end" | "center" | "baseline" | "stretch" | "space-between" | "space-around";
  justify?: "flex-start" | "flex-end" | "center" | "space-between" | "space-around" | "space-evenly";
  direction?: "row" | "column" | "row-reverse" | "column-reverse";
  alignSelf?: "flex-start" | "flex-end" | "center";
  padding?: string;
  margin?: string;

  width?: string;
  maxWidth?: string;
  minWidth?: string;

  height?: string;
  maxHeight?: string;
  minHeight?: string;
  background?: string;
  overflow?: "visible" | "hidden" | "scroll" | "auto" | "inherit";
  border?: string;
  borderRight?: string;
  borderLeft?: string;
  borderTop?: string;
  borderBottom?: string;

  bgColor?: string;
  opacity?: number;
  shadow?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22 | 23 | 24;
  rounded?: boolean;
  borderRadius?: string;
  transition?: string;
  transform?: string;

  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
  inset?: string;

  open?: boolean;
  zIndex?: number;

  textAlign?: "center" | "end" | "start";
  color?: string;
  bgImage?: string;
  bgPosition?: "bottom" | "center" | "top" | "initial" | "left" | "right";
  bgSize?: "cover" | "contain" | "initial" | "auto";
  hidden?: boolean;
  cursor?: string;
  gap?: string;
}

export const Wrapper = styled.div<WrapperProps>`
  align-items: ${p => p.align || "center"};
  justify-content: ${p => p.justify || "center"};
  ${p => p.transition && `transition: ${p.transition}`};
  ${p => p.transform && `transform: ${p.transform}`};

  ${p => p.position && `position: ${p.position}`};
  ${p => p.display && `display: ${p.display}`};

  ${p => p.flex && `flex: ${p.flex}`};
  ${p => p.wrap && `flex-wrap: ${p.wrap}`};
  ${p => p.direction && `flex-direction: ${p.direction}`};
  ${p => p.alignSelf && `align-self: ${p.alignSelf}`};

  ${p => p.padding && `padding: ${p.padding}`};
  ${p => p.margin && `margin: ${p.margin}`};

  ${p => p.background && `background: ${p.background}`};

  ${p => p.width && `width: ${p.width}`};
  ${p => p.maxWidth && `max-width: ${p.maxWidth}`};
  ${p => p.minWidth && `min-width: ${p.minWidth}`};

  ${p => p.height && `height: ${p.height}`};
  ${p => p.maxHeight && `max-height: ${p.maxHeight}`};
  ${p => p.minHeight && `min-height: ${p.minHeight}`};

  ${p => p.overflow && `overflow: ${p.overflow}`};
  ${p => p.border && `border: ${p.border}`};
  ${p => p.borderRight && `border-right: ${p.borderRight}`};
  ${p => p.borderLeft && `border-left: ${p.borderLeft}`};
  ${p => p.borderTop && `border-top: ${p.borderTop}`};
  ${p => p.borderBottom && `border-bottom: ${p.borderBottom}`};

  ${p => p.flexGrow && `flex-grow: ${p.flexGrow}`};
  ${p => p.opacity && `opacity: ${p.opacity}`};
  ${p => p.shadow && `box-shadow: ${p.theme.shadows[p.shadow]}`};
  ${p => p.rounded && `border-radius: ${p.theme.borderRadius}`};
  ${p => p.borderRadius && `border-radius: ${p.borderRadius}`};

  ${p => p.top && `top: ${p.top}`};
  ${p => p.right && `right: ${p.right}`};
  ${p => p.bottom && `bottom: ${p.bottom}`};
  ${p => p.left && `left: ${p.left}`};
  ${p => p.inset && `inset: ${p.inset}`};

  ${p => p.zIndex && `z-index: ${p.zIndex}`};

  ${p => p.textAlign && `text-align: ${p.textAlign}`};
  ${p => p.color && `color: ${p.color}`};
  ${p => p.bgColor && `background-color: ${p.bgColor};`}

  ${p => p.bgImage && `background-image: url("${p.bgImage}")`};
  ${p => p.bgPosition && `background-position: ${p.bgPosition}`};
  ${p => p.bgSize && `background-size: ${p.bgSize}`};
  ${p => p.cursor && `cursor: ${p.cursor}`};
  ${p => p.gap && `gap: ${p.gap}`};
`;

export type FlexProps = Omit<WrapperProps, "display">;

export const Flex = styled(Wrapper)<FlexProps>`
  display: ${p => (p.hidden ? "none" : "flex")};
`;

export type RowContainerProps = Omit<FlexProps, "direction">;

export const RowContainer = styled(Flex)`
  display: ${p => (p.hidden ? "none" : "flex")};
  flex-direction: row;
`;

export type ColumnContainerProps = Omit<FlexProps, "direction">;

export const ColumnContainer = styled(Flex)`
  display: ${p => (p.hidden ? "none" : "flex")};
  flex-direction: column;
`;

export type GridSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
interface GridProps extends WrapperProps {
  xs?: GridSize;
  sm?: GridSize;
  md?: GridSize;
  lg?: GridSize;
  xl?: GridSize;
}
export const GridItem = styled(Wrapper)<GridProps>`
  display: flex;
  flex-grow: 0;
  max-width: ${p => (p.xs ? `${100 / (12 / p.xs)}%` : "100%")};
  flex-basis: ${p => (p.xs ? `${100 / (12 / p.xs)}%` : "100%")};
  ${p =>
    p.sm &&
    css`
      @media (min-width: ${p.theme.breakpoints.sm}) {
        max-width: ${`${100 / (12 / p.sm)}%`};
        flex-basis: ${`${100 / (12 / p.sm)}%`};
      }
    `}
  ${p =>
    p.md &&
    css`
      @media (min-width: ${p.theme.breakpoints.md}) {
        max-width: ${`${100 / (12 / p.md)}%`};
        flex-basis: ${`${100 / (12 / p.md)}%`};
      }
    `}
  ${p =>
    p.lg &&
    css`
      @media (min-width: ${p.theme.breakpoints.lg}) {
        max-width: ${`${100 / (12 / p.lg)}%`};
        flex-basis: ${`${100 / (12 / p.lg)}%`};
      }
    `}
    ${p =>
    p.xl &&
    css`
      @media (min-width: ${p.theme.breakpoints.xl}) {
        max-width: ${`${100 / (12 / p.xl)}%`};
        flex-basis: ${`${100 / (12 / p.xl)}%`};
      }
    `}
`;
